import { normalizeName } from "../parser/item-input.js";
import type { Unit } from "../types/entities.js";
import { ohneAkzente } from "./text.js";

/**
 * Was in jeder Küche steht, ohne dass es jemand aufschreibt.
 *
 * Ein Rezept nennt Salz, Pfeffer und Öl wie alles andere. Auf dem Zettel im
 * Laden sind sie Ballast: Man hat sie, man kauft sie nicht wöchentlich, und
 * zwischen zwanzig Zeilen verstecken sie das, was wirklich fehlt.
 *
 * Die Zutat trägt dafür seit Stufe 5 `is_staple`. Gesetzt hat es bisher fast
 * niemand: von Hand über den Schalter im Rezept, und beim Spiegeln aus der
 * Spaisekammer nur, wenn die Warengruppe drüben zufällig "spices" hieß. Diese
 * Tabelle schließt die Lücke ohne Netz und ohne Rückfrage, genau wie
 * `guessCategory` es für die Warengruppen tut.
 *
 * Gemessen an den echten Rezepten der Familie: 681 Zutatenzeilen aus 38
 * Rezepten, 408 verschiedene Namen. Die Grenzfälle in dieser Datei stammen
 * alle von dort, keiner ist erfunden.
 *
 * Was ausdrücklich **nicht** hierher gehört: Eier, Butter, Milch, Sahne,
 * Käse, Hefe. Sie stehen zwar oft im Kühlschrank, gehen aber aus und sind
 * dann ein echter Einkauf. Daniel am 20.09.2026: "Eier und so, die auf jeden
 * Fall auf so eine Liste draufgehören."
 */

/**
 * Immer vorhanden, egal wie viel das Rezept braucht.
 *
 * Gewürze, Saucen und die Flaschen im Vorratsschrank: Sie halten geöffnet
 * monatelang, und niemand kauft eine zweite Flasche Sojasauce, weil ein
 * Rezept zwei Esslöffel davon nennt.
 */
const IMMER: readonly string[] = [
  // Salz und Pfeffer, die beiden häufigsten Zeilen überhaupt
  "salz", "meersalz", "salzflocken", "fleur de sel",
  "pfeffer", "pfefferkörner", "cayennepfeffer",

  // Gewürze aus dem Glas
  "paprikapulver", "chilipulver", "chiliflocken", "currypulver", "curry", "kurkuma",
  "kreuzkümmel", "kümmel", "schwarzkümmel", "koriandersamen", "fenchelsamen", "senfkörner",
  "muskat", "muskatnuss", "knoblauchpulver", "zwiebelpulver", "ingwerpulver",
  "zimt", "nelken", "piment", "kardamom", "wacholder", "lorbeer", "lorbeerblatt", "lorbeerblätter",
  "safran", "sumach", "masala", "ras el hanout", "gewürz", "gewürze", "köftegewürz",
  "paprika dulce", "paprika edelsüß",
  "backpulver", "natron", "vanillezucker", "vanillinzucker", "vanilleextrakt",

  // Saucen und Pasten, die als Flasche oder Glas im Schrank stehen
  "sojasauce", "fischsauce", "austernsauce", "worcestershire", "worcestershiresauce",
  "teriyaki", "hoisin", "sriracha", "sambal oelek", "sambal", "sweet chili",
  "gochujang", "gochugaru", "currypaste", "chiliöl", "mirin",
  "senf", "ketchup", "mayonnaise", "aioli", "tahini", "harissa",
  "tomatenmark", "honig", "ahornsirup",

  // Essig in jeder Form
  "essig", "balsamico", "reisessig", "weinessig", "apfelessig",

  // Brühe und Bindemittel
  "brühe", "brühwürfel", "brühpulver", "brühepulver", "fond", "maggi", "saucenbinder", "stärke",

];

/**
 * Was gar nicht erst auf dem Zettel auftaucht, nicht einmal als Hinweis.
 *
 * Leitungswasser ist keine Ware. Es steht in jedem zweiten Rezept, und selbst
 * die Zeile "Immer im Haus, kommt nicht auf die Liste: Wasser" ist Ballast:
 * Sie erklärt etwas, das niemand erklärt haben wollte. Daniel am 21.09.2026:
 * "Wasser sollte da gar nicht vorkommen."
 *
 * Gemeint ist ausdrücklich nur das aus der Leitung. Mineralwasser und Sprudel
 * sind ein Kasten und damit ein echter Einkauf; sie stehen in
 * KEINE_GRUNDZUTAT und fallen schon vorher heraus.
 */
const KEINE_WARE: readonly string[] = ["wasser", "nudelwasser", "leitungswasser", "eiswürfel"];

/**
 * Nur in kleiner Menge vorhanden.
 *
 * Mehl, Zucker und Öl sind beides: der Rest in der Tüte, mit dem man die
 * Arbeitsfläche bestäubt, und die Hauptzutat, ohne die das Fladenbrot nicht
 * stattfindet. Daniels Rezepte haben von beidem Beispiele, direkt
 * nebeneinander: "Mehl für die Arbeitsfläche" und "862 g Weizenmehl".
 */
const NUR_WENIG: readonly string[] = [
  "mehl", "grieß", "griess", "semmelbrösel", "panko",
  "zucker", "öl", "fett",
];

/**
 * Wörter, die einen Treffer aufheben.
 *
 * Steht eines davon im Namen, ist die Zeile ein echter Einkauf, auch wenn
 * ein Begriff von oben darin steckt. Die Liste ist an den echten Rezepten
 * gewachsen: die Zuckerschoten aus dem Crispy Chili Beef, die Sardellen in
 * Olivenöl aus dem Caesar Salad.
 */
const KEINE_GRUNDZUTAT: readonly string[] = [
  "zuckerschote", "zuckererbse", "zuckermais", "zuckerhut", "hagelzucker",
  "salzstange", "salzkartoffel", "salzgebäck",
  "pfefferminz", "paprikaschote", "sardelle",
  "mineralwasser", "sprudel", "kokoswasser", "rosenwasser", "blütenwasser",
  "essiggurke", "senfgurke", "honigmelone", "olivenpaste",
];

/**
 * Kräuter, die man getrocknet im Glas hat, frisch aber kauft.
 *
 * "1 TL Oregano, gerebelt" kommt aus dem Glas, "4 bis 6 Zweige frischer
 * Thymian" aus dem Kühlregal. Beide stehen in Daniels Rezepten. Basilikum,
 * Petersilie, Schnittlauch, Dill und Minze fehlen hier mit Absicht: Die
 * kauft man fast immer frisch.
 */
const KRAEUTER_AUS_DEM_GLAS: readonly string[] = [
  "oregano", "thymian", "rosmarin", "majoran", "salbei", "estragon", "bohnenkraut",
  "kräuter der provence", "italienische kräuter",
];

/** Woran man sieht, dass ein Kraut frisch gemeint ist. */
const FRISCH: readonly string[] = ["frisch", "frischer", "frische", "frisches", "zweig", "zweige", "bund", "topf", "strauch", "gehackt"];

/**
 * Bis hierhin gilt eine Menge als "hab ich noch".
 *
 * Hundert Gramm Mehl sind ein Rest in der Tüte, ein Kilo ist ein Einkauf.
 * Einheiten wie Teelöffel und Esslöffel kennt die App nicht; sie kommen ohne
 * Menge an (siehe `planSpaisekammerRezept`), und genau das ist das Zeichen
 * für eine Kleinigkeit.
 */
const KLEINE_MENGE: Partial<Record<Unit, number>> = { g: 100, ml: 100, kg: 0.1, l: 0.1 };

export interface StapleFrage {
  name: string;
  quantity?: number | null;
  unit?: Unit | null;
}

/**
 * Ist das überhaupt eine Ware, die man einkauft?
 *
 * Anders als eine Grundzutat wird das hier nicht als "habe ich schon"
 * gemeldet, sondern stillschweigend übergangen. Der Unterschied ist klein,
 * aber er entscheidet, ob unter der Liste "Wasser" steht oder nichts.
 */
export function istKeineWare(name: string): boolean {
  const voll = alsSauce(ohneAkzente(normalizeName(name)));
  if (voll.length === 0) return false;
  if (KEINE_GRUNDZUTAT.some((wort) => voll.includes(ohneAkzente(wort)))) return false;
  const woerter = voll.split(/[\s.]+/u).filter((wort) => wort.length > 0);
  return trifft(woerter, KEINE_WARE);
}

/**
 * Gehört diese Zutat zu dem, was man ohnehin im Haus hat?
 *
 * Anders als `guessCategory` reicht hier kein Treffer irgendwo im Wort. Für
 * die Warengruppe genügt ein Hinweis, für die Grundzutat muss die Ware selbst
 * gemeint sein: Im Deutschen steht sie am Ende des zusammengesetzten Wortes.
 * "Olivenöl" ist ein Öl, "Salzstangen" sind Stangen und keine Salzsorte.
 *
 * Eine falsche Grundzutat ist teurer als eine übersehene: Wer beim Fladenbrot
 * ohne Mehl dasteht, kann nicht backen. Wer eine Zeile zu viel auf dem Zettel
 * hat, tippt sie weg.
 */
export function guessStaple(frage: StapleFrage): boolean {
  const voll = alsSauce(ohneAkzente(normalizeName(frage.name)));
  if (voll.length === 0) return false;

  if (KEINE_GRUNDZUTAT.some((wort) => voll.includes(ohneAkzente(wort)))) return false;

  // Bei einer Alternative zählt die erste: "150 bis 200 ml Weißwein oder
  // Brühe" ist Wein, auch wenn Brühe im Haus ist, und "frischer Limettensaft
  // oder milder Reisessig" ist Limettensaft.
  const gesucht = voll.split(/\boder\b/u)[0]?.trim() ?? voll;
  if (gesucht.length === 0) return false;

  const woerter = gesucht.split(/[\s.]+/u).filter((wort) => wort.length > 0);

  if (trifft(woerter, KEINE_WARE) || trifft(woerter, IMMER)) return true;

  if (trifft(woerter, KRAEUTER_AUS_DEM_GLAS)) {
    return !FRISCH.some((wort) => woerter.includes(wort));
  }

  if (trifft(woerter, NUR_WENIG)) return kleineMenge(frage.quantity ?? null, frage.unit ?? null);

  return false;
}

/**
 * "Sojasoße" ist "Sojasauce".
 *
 * Beide Schreibungen stehen in Daniels Rezepten, mal so, mal so, und die
 * Tabelle soll deshalb nicht jede Sauce doppelt führen. "Soßenbinder" wird
 * dabei zu "Saucenbinder", also steht er so in der Tabelle.
 */
function alsSauce(wert: string): string {
  return wert.replace(/so(?:ß|ss)e/gu, "sauce");
}

/**
 * Trifft einer der Begriffe als Grundwort?
 *
 * Grundwort heißt: ein ganzes Wort des Namens, oder das Ende eines Wortes.
 * "Meersalz" ist Salz, "Salzstangen" nicht, und "Röllchen" ist kein Öl.
 */
function trifft(woerter: readonly string[], begriffe: readonly string[]): boolean {
  return begriffe.some((roh) => {
    const begriff = ohneAkzente(roh);
    // Ein Begriff aus zwei Wörtern kann kein Wortende sein, er steht als Ganzes da.
    if (begriff.includes(" ")) return woerter.join(" ").includes(begriff);
    return woerter.some((wort) => wort === begriff || (wort.length > begriff.length && wort.endsWith(begriff)));
  });
}

/** Ohne Menge ist es eine Kleinigkeit: "etwas Öl", "Mehl für die Arbeitsfläche". */
function kleineMenge(quantity: number | null, unit: Unit | null): boolean {
  if (quantity === null) return true;
  if (unit === null) return false;
  const grenze = KLEINE_MENGE[unit];
  return grenze !== undefined && quantity <= grenze;
}
